import { resolveActiveContextGroup } from '@/core/user';
import type { UserContext } from '@/core/user';
import type { Viewer } from '@/core/viewer';

/**
 * Any group carrying the api's answer to the account question.
 *
 * Structural rather than the generated `Group`, because the surfaces that ask (a
 * consultation's project, an expert request's project) each select their own
 * shape. Everything they share is this field, and requiring more would force a
 * cast at every call site.
 *
 * The field is REQUIRED here even though the argument is optional, so that
 * passing the wrong nesting level — `isEngagementsEnabledForGroup(project)`
 * rather than `project.group` — fails to compile instead of quietly answering
 * false. Two of the three call sites receive `project` as `any`, so this only
 * protects the typed one; the compile-time pins in
 * `ExpertRequestPreview.test.tsx` cover the rest.
 */
export interface EngagementsGatedGroup {
  engagements_enabled: boolean;
}

/**
 * Does THIS group's account have the consulting-engagements product?
 *
 * The account question in its most primitive form, and since ENG-5715 it is no
 * longer a webapp opinion: `Group.engagements_enabled` is the api's own answer,
 * resolved from an `engagement_billing_settings` row with the environment's
 * master flag ANDed in. Use it wherever the relevant group is already in hand —
 * an existing expert request, a project, a consultation — rather than inferring
 * one from the viewer's context.
 *
 * It used to test a group-id allowlist compiled into the bundle from
 * `VITE_ENABLE_CONSULTING_ENGAGEMENTS_ACCOUNT_IDS`. ENG-5669 moved the api off
 * its own copy of that list onto the settings row and could not reach this one,
 * so for a while the two repos could disagree about the same account: the api
 * would refuse a `consulting_project` ER for an account this predicate called
 * disabled, or the reverse. One answer, served by the side that owns it, is the
 * point of the change — and onboarding an account is now a mutation rather than
 * a deploy plus an Amplify rebuild.
 *
 * Do NOT use it to decide whether someone may REACH engagement UI — that is
 * `canViewEngagementSurfaces`, which is this question OR a role override.
 * Reaching for the wrong one is a live footgun: `useGovernanceApprover` exists
 * partly because the policy short-circuits on `viewer.admin`, so every staff
 * member looked entitled to a queue that was approver-only.
 */
export function isEngagementsEnabledForGroup(group?: EngagementsGatedGroup | null): boolean {
  // Strict `=== true`, not truthiness: the field is optional in every generated
  // type because the group it hangs off is, and an absent answer — a query that
  // forgot to select it, a group that failed to load — must never read as yes.
  return group?.engagements_enabled === true;
}

/**
 * The same account question, asked about the account the viewer is acting in.
 *
 * For surfaces with no group of their own. It obeys the active "view as" context
 * absolutely: no admin bypass, no expert bypass. Scanning all of `viewer.groups`
 * instead would mis-gate a multi-account user, who has to be classified by the
 * account they are operating in (ENG-5351).
 *
 * KNOWN GAP (ENG-5680): staff can view-as any group through the menu search bar
 * (`UserContextMenu.tsx`, `<SelectGroup autoComplete>` behind `viewer.admin`),
 * which calls `setUserContext(groupId)` with no membership check.
 * `resolveActiveContextGroup` resolves that id against `viewer.groups`, so a
 * searched group is not found and this returns false even for an enabled
 * account. Every account-scoped gate in the app is blind the same way. It fails
 * OPEN for callers that hide something on a true, which is the safe direction,
 * and it becomes correct here with no change once ENG-5680 lands.
 */
export function isEngagementsActiveForContext(viewer: Viewer, userContext: UserContext): boolean {
  if (!viewer.id) return false;
  const activeGroup = resolveActiveContextGroup(viewer, userContext);
  if (!activeGroup) return false;
  // Reads the id list rather than a field on the group, because the viewer's
  // answer arrives from a different document than `groups` does — see
  // GET_ENGAGEMENT_GATES in core/viewer.ts, which is kept out of GET_VIEWER so a
  // schema mismatch cannot log the user out. Empty means off, which is also what
  // a failed gate query leaves.
  //
  // Defaulted rather than trusted: `setViewer` merges (Object.assign), so the
  // partial payloads in `auth/store/index.ts` and `actions/address.ts` produce a
  // viewer that never went through `fetchViewer` and carries no list at all. An
  // absent list is the same answer as an empty one.
  return (viewer.engagementsEnabledGroupIds ?? []).includes(activeGroup.id);
}

/**
 * May this viewer reach consulting-engagements surfaces?
 *
 * This is the VISIBILITY question: the account question above, OR a role
 * override. Named for what it decides — every caller is a nav item, a route, or
 * a mount asking "should this render", never "does this account have the
 * product". It was `isConsultingEngagementsEnabled` until ENG-5670, a name that
 * read as an account property while behaving as a viewer policy, which is how
 * the two questions got conflated in the first place.
 *
 * Split out of `featureFlag.ts` so callers that already hold `viewer` and
 * `userContext` — `AccessHeader` is a connected component and has both as props
 * — can use it without importing `@/store`. Pulling the root store into the
 * global header drags every reducer in with it, which broke `AccessHeader`'s
 * partial mocks; a pure function also lets the resolution rules be tested
 * without a Redux harness.
 *
 * Resolution rules, in order:
 *
 * - **Environment off ⇒ off, for everyone**, admins and experts included, via
 *   `viewer.engagementsAvailable` (ENG-5715). This is the bound the overrides
 *   below need and the account question cannot supply: staff are not members of
 *   the accounts they read and an expert belongs to no group, so
 *   `engagementsEnabled` answers false for precisely the viewers the overrides
 *   exist for. Without it, an admin in production would be shown nav pointing at
 *   a surface the schema does not contain — `AttachEngagement` adds nothing when
 *   `ENABLE_CONSULTING_ENGAGEMENTS` is off. It replaces the empty-allowlist
 *   guard and is deliberately *inside* the gate, above the bypasses, for the
 *   same reason that one was.
 *
 *   It is deliberately COARSER than what it replaced, and that is a real
 *   behaviour change. The empty-allowlist guard implied "at least one account is
 *   onboarded"; this one only asks whether the service is on for the deployment.
 *   So in an environment with the flag on and zero `engagement_billing_settings`
 *   rows, every staff member and every expert passes where none did before. The
 *   surfaces they reach are empty and the api refuses anything they try, so the
 *   cost is a visible-but-barren feature rather than an escalation — but it is
 *   the one behaviour change in this swap, and it is why QA can look dark for
 *   customers while staff still see the nav.
 * - **Experts pass** whenever the feature is on for anyone. An expert has no
 *   group, so there is no per-account axis to place them on, and the Hub is
 *   dual-sided — gating them would dark the whole expert half, accept-offer
 *   included. This only decides visibility: the api returns null for any
 *   engagement the viewer is not a participant on, so the server remains the
 *   real boundary.
 * - **Otherwise the account question decides**, via
 *   `isEngagementsActiveForContext`. The routes carry no account context, so the
 *   account comes from the "view as" context, else the default account group.
 */
export function canViewEngagementSurfaces(viewer: Viewer, userContext: UserContext): boolean {
  if (!viewer.engagementsAvailable) return false;
  if (!viewer.id) return false;

  if (viewer.admin) return true;
  if (viewer.signup_type === 'expert' || viewer.expert_state) return true;

  return isEngagementsActiveForContext(viewer, userContext);
}
